/**
 * RecipientEditor — multi-recipient editor for ScheduleFormDialog (Story 7.8).
 *
 * Replaces the single-recipient <input>: add/remove rows, one Primary radio across
 * rows, an optional per-row name, and a "Send me a copy" toggle (adds the configuring
 * user's own email as a non-primary recipient — UI-only concept). Edit mode renders
 * rows that are currently suppressed (unsubscribed/bounced) READ-ONLY (email/name/
 * primary disabled) with a badge — they can only be removed, not silently re-pointed
 * at a new address (which would defeat the unsubscribe/bounce suppression).
 *
 * Client-side validation MIRRORS the server (apps/server/src/routes/schedules/crud.ts):
 * max-10 (disable Add at 10), email format + length, case-insensitive dedupe (inline
 * error), >= 1 row, exactly one primary. The owning dialog reads the `valid` flag from
 * {@link validateRecipients} to gate submit.
 *
 * §4 UI directives: motion-staggered rows, 0.5px borders, rounded inputs, Phosphor
 * icons (Plus/Trash), Switch + RadioGroup primitives.
 */

import { Button, Input, Switch, Label } from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import { Plus, Trash, SealWarning } from "@phosphor-icons/react";
import type { SuppressionReason } from "../../lib/schedulesApi";

const FIELD_BORDER = "0.5px solid var(--hairline)";

export const MAX_RECIPIENTS = 10;
/** Mirror of the server's recipient email max length (crud.ts recipientSchema). */
const MAX_EMAIL_LENGTH = 320;
/** Mirror of the server's recipient name max length (crud.ts recipientSchema). */
const MAX_NAME_LENGTH = 200;

/** A recipient row in the editor (a superset of the API ScheduleRecipientInput). */
export interface RecipientRow {
  email: string;
  name?: string;
  isPrimary: boolean;
  /** Suppression state surfaced read-only in edit mode (undefined for new rows). */
  suppressed?: boolean;
  suppressionReason?: SuppressionReason | null;
  /** Transient client-only marker: this row was added by the "Send me a copy"
   *  toggle (NOT a manually-entered recipient). Lets the toggle remove only its own
   *  row and never a deliberately-added self-recipient. Never sent to the server. */
  addedByCopy?: boolean;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Lowercase + trim — the client mirror of the server's normalizeEmail for dedupe. */
function canonical(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Validates the recipient set against the server rules. Returns a per-index error map
 * (keyed `email-<i>`) + a form-level `recipients` error, and a `valid` flag the dialog
 * uses to gate submit. Pure — no React.
 */
export function validateRecipients(rows: RecipientRow[]): {
  valid: boolean;
  errors: Record<string, string>;
} {
  const errors: Record<string, string> = {};
  if (rows.length === 0) {
    errors.recipients = "Add at least one recipient";
    return { valid: false, errors };
  }
  if (rows.length > MAX_RECIPIENTS) {
    errors.recipients = `At most ${MAX_RECIPIENTS} recipients`;
  }
  const seen = new Set<string>();
  rows.forEach((r, i) => {
    // Validate the optional name FIRST (independent of email) so a server-side
    // name rejection (HTML / >200 chars) is caught + shown inline rather than
    // swallowed as an unrendered `recipients.N.name` field error after submit.
    const name = r.name?.trim();
    if (name) {
      if (name.length > MAX_NAME_LENGTH) {
        errors[`name-${i}`] = `Name must be ${MAX_NAME_LENGTH} characters or fewer`;
      } else if (/[<>]/.test(name) || /\bon\w+\s*=/i.test(name)) {
        // Mirror the server's HTML_INJECTION_PATTERN (angle brackets AND inline
        // `on…=` handlers) so a name the server would reject is caught inline
        // pre-submit, not swallowed as an unrendered server field error.
        errors[`name-${i}`] = "Name can't contain HTML";
      }
    }
    const trimmed = r.email.trim();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      errors[`email-${i}`] = "Enter a valid email";
      return;
    }
    if (trimmed.length > MAX_EMAIL_LENGTH) {
      errors[`email-${i}`] = `Email must be ${MAX_EMAIL_LENGTH} characters or fewer`;
      return;
    }
    const key = canonical(trimmed);
    if (seen.has(key)) {
      errors[`email-${i}`] = "Duplicate recipient";
    }
    seen.add(key);
  });
  const primaryCount = rows.filter((r) => r.isPrimary).length;
  if (primaryCount > 1) {
    errors.recipients = "Only one recipient can be primary";
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * Normalize recipients loaded for EDIT: if the stored primary is suppressed
 * (unsubscribed/bounced — it will never receive the report), move primary to the
 * first non-suppressed row. Prevents a no-op "Save" from re-persisting a primary
 * that 7.7's greeting names but the delivery seam skips. No-op when the primary is
 * deliverable or when every recipient is suppressed (nothing better to promote).
 */
export function normalizeLoadedRecipients(rows: RecipientRow[]): RecipientRow[] {
  const primary = rows.find((r) => r.isPrimary);
  if (!primary?.suppressed) return rows;
  const firstLive = rows.find((r) => !r.suppressed);
  if (!firstLive) return rows;
  return rows.map((r) => ({ ...r, isPrimary: r === firstLive }));
}

function suppressionBadge(reason: SuppressionReason | null | undefined): string {
  if (reason === "user_unsubscribe") return "Unsubscribed";
  if (reason === "bounce") return "Bounced";
  if (reason === "complaint") return "Complaint";
  return "Suppressed";
}

export function RecipientEditor({
  rows,
  onChange,
  errors,
  currentUserEmail,
}: {
  rows: RecipientRow[];
  onChange: (next: RecipientRow[]) => void;
  errors: Record<string, string>;
  currentUserEmail: string;
}) {
  const atMax = rows.length >= MAX_RECIPIENTS;

  function update(i: number, patch: Partial<RecipientRow>) {
    onChange(
      rows.map((r, idx) => {
        if (idx !== i) return r;
        const merged = { ...r, ...patch };
        // If the user edits a "send me a copy" row's email away from their own
        // address, it becomes a normal manual recipient — drop the marker so the
        // toggle no longer owns/removes it.
        if (
          patch.email !== undefined &&
          merged.addedByCopy &&
          canonical(merged.email) !== canonical(currentUserEmail)
        ) {
          merged.addedByCopy = false;
        }
        return merged;
      }),
    );
  }

  function remove(i: number) {
    const removed = rows[i];
    let next = rows.filter((_, idx) => idx !== i);
    // Never leave the set with no primary: if the removed row was primary and rows
    // remain, promote the first (mirrors the server auto-promote on write).
    if (removed?.isPrimary && next.length > 0 && !next.some((r) => r.isPrimary)) {
      next = next.map((r, idx) => (idx === 0 ? { ...r, isPrimary: true } : r));
    }
    onChange(next);
  }

  function add() {
    if (atMax) return;
    // First row becomes primary by default; subsequent rows are non-primary.
    onChange([...rows, { email: "", isPrimary: rows.length === 0 }]);
  }

  function setPrimary(i: number) {
    onChange(rows.map((r, idx) => ({ ...r, isPrimary: idx === i })));
  }

  // "Send me a copy" state:
  //  - alreadyManual: the user's email is already a deliberately-added recipient
  //    (not a toggle-added copy) → they already get a copy; the toggle is shown ON
  //    but disabled (it must not delete a row the user curated).
  //  - sendMeOn: a toggle-added copy row exists.
  const sendMeKey = canonical(currentUserEmail);
  const alreadyManual =
    Boolean(currentUserEmail) &&
    rows.some((r) => canonical(r.email) === sendMeKey && !r.addedByCopy);
  const sendMeCopyRow =
    Boolean(currentUserEmail) &&
    rows.some((r) => r.addedByCopy && canonical(r.email) === sendMeKey);
  const sendMeOn = alreadyManual || sendMeCopyRow;
  // Can't add an 11th via the toggle, and can't toggle off a row we don't own.
  const sendMeDisabled = alreadyManual || (atMax && !sendMeCopyRow);

  function toggleSendMe(on: boolean) {
    if (!currentUserEmail || alreadyManual) return;
    if (on) {
      if (atMax) return; // guarded by the disabled Switch, but be defensive
      if (rows.some((r) => canonical(r.email) === sendMeKey)) return; // already present
      onChange([
        ...rows,
        { email: currentUserEmail, isPrimary: rows.length === 0, addedByCopy: true },
      ]);
    } else {
      // Remove ONLY the toggle-added copy row (never a manual self-recipient).
      const removed = rows.find(
        (r) => r.addedByCopy && canonical(r.email) === sendMeKey,
      );
      let next = rows.filter(
        (r) => !(r.addedByCopy && canonical(r.email) === sendMeKey),
      );
      if (removed?.isPrimary && next.length > 0 && !next.some((r) => r.isPrimary)) {
        next = next.map((r, idx) => (idx === 0 ? { ...r, isPrimary: true } : r));
      }
      onChange(next);
    }
  }

  return (
    <div className="space-y-3" data-testid="recipient-editor">
      <AnimatePresence initial={false}>
        {rows.map((row, i) => (
          <motion.div
            key={i}
            className="space-y-2 rounded-2xl p-3"
            style={{ border: FIELD_BORDER }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ delay: Math.min(i * 0.03, 0.15) }}
            data-testid="recipient-row"
          >
            <div className="flex items-center gap-2">
              <Input
                type="email"
                value={row.email}
                onChange={(e) => update(i, { email: e.target.value })}
                placeholder="recipient@example.com"
                aria-label={`Recipient ${i + 1} email`}
                disabled={row.suppressed}
                className={`flex-1 rounded-2xl ${
                  errors[`email-${i}`] ? "border-destructive" : ""
                }`}
                data-testid={`recipient-email-${i}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="rounded-2xl cursor-pointer text-muted-foreground hover:text-destructive"
                onClick={() => remove(i)}
                aria-label={`Remove recipient ${i + 1}`}
                data-testid={`recipient-remove-${i}`}
                // Keep ≥1 recipient — but a lone SUPPRESSED row may always be
                // removed so the editor is never frozen (submit still gates on ≥1).
                disabled={rows.length === 1 && !row.suppressed}
              >
                <Trash size={16} />
              </Button>
            </div>

            <Input
              type="text"
              value={row.name ?? ""}
              onChange={(e) =>
                update(i, { name: e.target.value || undefined })
              }
              placeholder="Name (optional)"
              aria-label={`Recipient ${i + 1} name`}
              disabled={row.suppressed}
              className={`rounded-2xl ${
                errors[`name-${i}`] ? "border-destructive" : ""
              }`}
              data-testid={`recipient-name-${i}`}
            />
            {errors[`name-${i}`] && (
              <p className="text-xs text-destructive" role="alert">
                {errors[`name-${i}`]}
              </p>
            )}

            <div className="flex items-center justify-between">
              <label
                className={`flex items-center gap-2 text-sm text-muted-foreground ${
                  row.suppressed ? "opacity-50" : "cursor-pointer"
                }`}
              >
                <input
                  type="radio"
                  name="recipient-primary"
                  checked={row.isPrimary}
                  onChange={() => setPrimary(i)}
                  disabled={row.suppressed}
                  aria-label={`Make recipient ${i + 1} primary`}
                  data-testid={`recipient-primary-${i}`}
                />
                Primary
              </label>
              {row.suppressed && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                  data-testid={`recipient-suppressed-${i}`}
                >
                  <SealWarning size={12} />
                  {suppressionBadge(row.suppressionReason)}
                </span>
              )}
            </div>

            {errors[`email-${i}`] && (
              <p className="text-xs text-destructive" role="alert">
                {errors[`email-${i}`]}
              </p>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {errors.recipients && (
        <p className="text-xs text-destructive" role="alert">
          {errors.recipients}
        </p>
      )}

      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          className="rounded-xl bg-background cursor-pointer shadow-xs hover:bg-background"
          style={{ border: FIELD_BORDER }}
          onClick={add}
          disabled={atMax}
          data-testid="recipient-add"
        >
          <Plus size={15} className="mr-1" /> Add recipient
        </Button>
        {atMax && (
          <span className="text-xs text-muted-foreground">
            Max {MAX_RECIPIENTS} recipients
          </span>
        )}
      </div>

      {currentUserEmail && (
        <div className="flex items-center gap-2 pt-1">
          <Switch
            id="send-me-a-copy"
            checked={sendMeOn}
            disabled={sendMeDisabled}
            onCheckedChange={toggleSendMe}
            data-testid="send-me-a-copy"
          />
          <Label htmlFor="send-me-a-copy" className="cursor-pointer text-sm">
            Send me a copy ({currentUserEmail})
          </Label>
        </div>
      )}
    </div>
  );
}
